import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, GripVertical, RotateCcw, SlidersHorizontal } from "lucide-react";
import { Button } from "../../components/ui/button/index.js";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover/index.js";
import { cn } from "../../lib/utils.js";
import { CAMPAIGN_LIST_REQUIRED_COLUMNS, isCampaignListDataColumnId, type CampaignListDataColumnId } from "./campaign-list-table.js";

export function CampaignsColumnsPanel({
  columnOrder,
  hiddenColumns,
  onToggle,
  onMove,
  onReorder,
  onReset,
}: {
  columnOrder: readonly CampaignListDataColumnId[];
  hiddenColumns: ReadonlySet<CampaignListDataColumnId>;
  onToggle: (id: CampaignListDataColumnId) => void;
  onMove: (id: CampaignListDataColumnId, direction: "up" | "down") => void;
  onReorder: (fromId: CampaignListDataColumnId, toId: CampaignListDataColumnId) => void;
  onReset: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [draggingId, setDraggingId] = useState<CampaignListDataColumnId | null>(null);
  const [dropId, setDropId] = useState<CampaignListDataColumnId | null>(null);

  return (
    <div className="grid gap-1" data-testid="campaign-list-columns-popover">
      {columnOrder.map((columnId, index) => {
        const required = CAMPAIGN_LIST_REQUIRED_COLUMNS.has(columnId);
        const visible = !hiddenColumns.has(columnId);
        return (
          <div
            key={columnId}
            className={cn(
              "flex items-center gap-1 rounded-md",
              dropId === columnId && draggingId != null && draggingId !== columnId
                ? "bg-accent/10"
                : null,
            )}
            data-testid="campaign-list-column-row"
            onDragOver={(event) => {
              event.preventDefault();
              event.dataTransfer.dropEffect = "move";
              setDropId(columnId);
            }}
            onDragEnter={(event) => {
              event.preventDefault();
              setDropId(columnId);
            }}
            onDrop={(event) => {
              event.preventDefault();
              const fromRaw = event.dataTransfer.getData("text/plain");
              const from = isCampaignListDataColumnId(fromRaw) ? fromRaw : draggingId;
              setDraggingId(null);
              setDropId(null);
              if (from) onReorder(from, columnId);
            }}
          >
            <span
              draggable
              data-testid="campaign-column-drag-handle"
              aria-label={t("campaigns.list.reorderColumn", {
                column: t(`campaigns.list.columns.${columnId}`),
              })}
              className={cn(
                "inline-grid size-7 shrink-0 select-none place-items-center text-muted",
                draggingId === columnId ? "cursor-grabbing" : "cursor-grab",
              )}
              onDragStart={(event) => {
                event.dataTransfer.effectAllowed = "move";
                event.dataTransfer.setData("text/plain", columnId);
                setDraggingId(columnId);
              }}
              onDragEnd={() => {
                setDraggingId(null);
                setDropId(null);
              }}
            >
              <GripVertical size={14} aria-hidden="true" />
            </span>
            <label className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-1 py-1 text-[0.8125rem]">
              <input
                type="checkbox"
                checked={visible}
                disabled={required && visible}
                onChange={() => onToggle(columnId)}
                className="size-4 accent-[var(--color-accent)]"
                data-testid={`campaign-list-column-toggle-${columnId}`}
              />
              <span className="min-w-0 truncate">{t(`campaigns.list.columns.${columnId}`)}</span>
            </label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="size-7 p-0"
              disabled={index === 0}
              aria-label={t("campaigns.list.moveColumnUp", {
                column: t(`campaigns.list.columns.${columnId}`),
              })}
              onClick={() => onMove(columnId, "up")}
            >
              <ArrowUp size={14} aria-hidden="true" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="size-7 p-0"
              disabled={index === columnOrder.length - 1}
              aria-label={t("campaigns.list.moveColumnDown", {
                column: t(`campaigns.list.columns.${columnId}`),
              })}
              onClick={() => onMove(columnId, "down")}
            >
              <ArrowDown size={14} aria-hidden="true" />
            </Button>
          </div>
        );
      })}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="mt-1 h-8 justify-start"
        onClick={onReset}
        data-testid="campaign-list-reset-columns"
      >
        <RotateCcw size={14} aria-hidden="true" />
        {t("campaigns.list.resetColumns")}
      </Button>
    </div>
  );
}

export function CampaignsColumnsPopover({
  columnOrder,
  hiddenColumns,
  onToggle,
  onMove,
  onReorder,
  onReset,
}: {
  columnOrder: readonly CampaignListDataColumnId[];
  hiddenColumns: ReadonlySet<CampaignListDataColumnId>;
  onToggle: (id: CampaignListDataColumnId) => void;
  onMove: (id: CampaignListDataColumnId, direction: "up" | "down") => void;
  onReorder: (fromId: CampaignListDataColumnId, toId: CampaignListDataColumnId) => void;
  onReset: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 shrink-0"
          data-testid="campaign-list-columns"
          aria-label={t("campaigns.list.columnsControl")}
        >
          <SlidersHorizontal size={14} aria-hidden="true" />
          {t("campaigns.list.columnsControl")}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(20rem,calc(100vw-2rem))] p-2">
        <CampaignsColumnsPanel
          columnOrder={columnOrder}
          hiddenColumns={hiddenColumns}
          onToggle={onToggle}
          onMove={onMove}
          onReorder={onReorder}
          onReset={onReset}
        />
      </PopoverContent>
    </Popover>
  );
}
